import type {
  ClassCategory,
  ProfileClassOrganizationLayout,
} from '@/features/profile/classes/classCategoryTypes';
import type { OwnedClassCard } from '@/features/profile/types';

export function removeClassIdFromBuckets(
  categories: readonly ClassCategory[],
  uncategorizedClassIds: readonly string[],
  classId: string,
): { categories: ClassCategory[]; uncategorizedClassIds: string[] } {
  return {
    categories: categories.map((category) => ({
      ...category,
      classIds: category.classIds.filter((id) => id !== classId),
    })),
    uncategorizedClassIds: uncategorizedClassIds.filter((id) => id !== classId),
  };
}

export function buildProfileClassOrganizationLayout(
  classes: readonly OwnedClassCard[],
  categories: readonly ClassCategory[],
  uncategorizedClassIds: readonly string[],
): ProfileClassOrganizationLayout {
  const cardsById = new Map(classes.map((card) => [card.id, card]));
  const uncategorized = uncategorizedClassIds
    .map((id) => cardsById.get(id))
    .filter((item): item is OwnedClassCard => Boolean(item));

  const sortedCategories = [...categories].sort((a, b) => a.sortOrder - b.sortOrder);
  const categoryGroups = sortedCategories.map((category) => ({
    category,
    classes: category.classIds
      .map((id) => cardsById.get(id))
      .filter((item): item is OwnedClassCard => Boolean(item)),
  }));

  const totalCount =
    uncategorized.length + categoryGroups.reduce((sum, group) => sum + group.classes.length, 0);

  return { uncategorized, categories: categoryGroups, totalCount };
}

export function moveItemInOrder<T extends string>(
  order: readonly T[],
  itemId: T,
  direction: 'up' | 'down',
): T[] {
  const index = order.indexOf(itemId);
  if (index < 0) {
    return [...order];
  }
  const targetIndex = direction === 'up' ? index - 1 : index + 1;
  if (targetIndex < 0 || targetIndex >= order.length) {
    return [...order];
  }
  const next = [...order];
  [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
  return next;
}

export function collectAssignedClassIds(
  categories: readonly ClassCategory[],
  uncategorizedClassIds: readonly string[],
): Set<string> {
  const assigned = new Set<string>(uncategorizedClassIds);
  for (const category of categories) {
    for (const classId of category.classIds) {
      assigned.add(classId);
    }
  }
  return assigned;
}
